import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Link as LinkIcon, Check, Mail, Send, Share2 } from "lucide-react";
import { toast } from "sonner";

/** Warm share sheet for a full meal-plan basket. Encodes recipe ids into the URL. */
export default function BasketShareSheet({ ids, open, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/basket?share=${encodeURIComponent(ids.join(","))}`
    : "";
  const text = `${ids.length} honest ${ids.length === 1 ? "recipe" : "recipes"} from Willoah — open the shopping list on your phone.`;
  const encoded = encodeURIComponent(`${text} ${url}`);
  const hasNative = typeof navigator !== "undefined" && !!navigator.share;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast("Basket link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast("Could not copy."); }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try { await navigator.share({ title: "Willoah meal plan", text, url }); onClose?.(); } catch {}
  };

  const linkBtn = "inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/12 transition-colors";

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#2A2823]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog" aria-modal="true" data-testid="basket-share-sheet" onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF8F1] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-[#B79256]/30 shadow-[0_30px_80px_rgb(42,40,35,0.2)] overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#B79256]/25">
          <div>
            <div className="accent-label text-[#B79256]">share this basket</div>
            <div className="text-xs text-[#2A2823]/50 mt-1">Real people. No algorithm.</div>
          </div>
          <button
            onClick={onClose}
            data-testid="basket-share-close"
            aria-label="Close share"
            className="p-2 rounded-full hover:bg-[#B79256]/15 text-[#2A2823]/60 hover:text-[#2A2823] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.75}/>
          </button>
        </div>

        <div className="px-5 pt-5">
          <p className="text-[#2A2823]/75 leading-relaxed">
            A friend can tap this link and land on the same shopping list on her phone — every ingredient, ready to copy.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <div data-testid="basket-share-url" className="flex-1 truncate rounded-full bg-white border border-[#B79256]/30 px-4 h-11 flex items-center text-sm text-[#2A2823]/85">
              {url}
            </div>
            <button
              data-testid="basket-share-copy"
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-full text-sm bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
            >
              {copied ? <><Check className="w-4 h-4" strokeWidth={2}/>Copied</> : <><LinkIcon className="w-4 h-4" strokeWidth={1.75}/>Copy link</>}
            </button>
          </div>
        </div>

        <div className="px-5 pt-5 pb-6">
          <div className="accent-label text-[#B79256]">or share via</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {hasNative && (
              <button type="button" data-testid="basket-share-native" onClick={nativeShare} className={linkBtn}>
                <Share2 className="w-4 h-4" strokeWidth={1.75}/> Your device
              </button>
            )}
            <a data-testid="basket-share-whatsapp" href={`https://wa.me/?text=${encoded}`} target="_blank" rel="noopener noreferrer" className={linkBtn}>
              <Send className="w-4 h-4" strokeWidth={1.75}/> WhatsApp
            </a>
            <a data-testid="basket-share-email" href={`mailto:?subject=${encodeURIComponent("A meal plan from Willoah")}&body=${encoded}`} className={linkBtn}>
              <Mail className="w-4 h-4" strokeWidth={1.75}/> Email
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
